import { Link } from "react-router";

import { formatCents } from "@/lib/money";

import {
  BarList,
  ReportSection,
  ReportTable,
  Stat,
  StatGrid,
} from "../ReportPrimitives";
import type { SalesReport } from "../reportTables";

const winRate = (bp: number | null) =>
  bp == null ? ", " : `${(bp / 100).toFixed(0)}%`;

const daysSince = (iso: string) =>
  Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000);

/** Sales: pipeline health, win rate, per-rep leaderboard and what is going stale. */
export const SalesPanel = ({ data }: { data: SalesReport }) => (
  <div className="flex flex-col gap-4">
    <StatGrid>
      <Stat
        label="Won"
        value={formatCents(data.won_cents)}
        helper={`${data.deals_won} deal${data.deals_won === 1 ? "" : "s"} closed`}
        tone="positive"
      />
      <Stat
        label="Win rate"
        value={winRate(data.win_rate_bp)}
        helper={
          data.win_rate_bp == null
            ? "Nothing closed yet"
            : `${data.deals_won} won / ${data.deals_lost} lost`
        }
      />
      <Stat
        label="Open pipeline"
        value={formatCents(data.open_pipeline_cents)}
        helper={`${formatCents(data.weighted_pipeline_cents)} weighted`}
      />
      <Stat
        label="Avg deal"
        value={
          data.avg_deal_cents == null ? ", " : formatCents(data.avg_deal_cents)
        }
        helper={`${data.deals_created} created in period`}
      />
    </StatGrid>

    <div className="grid gap-4 lg:grid-cols-2 [&>*]:min-w-0">
      <ReportSection title="Pipeline by stage" subtitle="Open deals only">
        <BarList
          items={data.by_stage.map((row) => ({
            label: row.label,
            value: row.cents,
            display: formatCents(row.cents),
            sub: `${row.count} deal${row.count === 1 ? "" : "s"}`,
          }))}
          empty="No open deals."
        />
      </ReportSection>

      <ReportSection
        title="By owner"
        subtitle="Closed in period, open right now"
      >
        <ReportTable
          columns={["Owner", "Won", "Lost", "Won value", "Open"]}
          align={["left", "right", "right", "right", "right"]}
          rows={data.by_owner.map((row) => [
            row.name,
            row.won,
            row.lost,
            formatCents(row.won_cents),
            formatCents(row.open_cents),
          ])}
          empty="No deals assigned."
        />
      </ReportSection>
    </div>

    <div className="grid gap-4 lg:grid-cols-2 [&>*]:min-w-0">
      <ReportSection
        title="Why deals were lost"
        subtitle="Closed-lost in period"
      >
        <ReportTable
          columns={["Reason", "Deals"]}
          align={["left", "right"]}
          rows={data.lost_reasons.map((row) => [row.reason, row.count])}
          empty="No deals lost in this period."
        />
      </ReportSection>

      <ReportSection
        title="Going stale"
        subtitle="Open deals untouched for 14+ days"
      >
        <ReportTable
          columns={["Deal", "Value", "Untouched"]}
          align={["left", "right", "right"]}
          rows={data.stale_deals.map((row) => [
            <Link
              key={row.id}
              to={`/deals/${row.id}/show`}
              className="font-medium text-foreground no-underline hover:underline"
            >
              {row.name}
            </Link>,
            formatCents(row.amount),
            `${daysSince(row.updated_at)}d`,
          ])}
          empty="Nothing is going stale, every open deal was touched recently."
        />
      </ReportSection>
    </div>
  </div>
);
